/**
 * Phone numbers on voice calls.
 *
 * The realtime model hears the audio itself and is bad at digit strings: on a
 * real call the transcript was "7276389688" while the model read back
 * "7 2 0 7 6 3 8 9 6 8 8" and then argued the number was too long. So the
 * digits are taken from the transcript here, counted in code, and handed to
 * the model as a system note before it replies.
 */
import { romanizeIndic } from './romanize.js';
const DIGIT_WORDS = {};
for (const [digit, words] of Object.entries({
    0: 'zero oh shunya shuny shunye sunya sifar sifr jiro ziro',
    1: 'one ek',
    2: 'two do',
    3: 'three teen tin',
    4: 'four char chaar',
    5: 'five panch paanch pach',
    6: 'six chhe chhah chah che chhai chhah',
    7: 'seven saat sat',
    8: 'eight aath ath',
    9: 'nine nau nao nain',
}))
    for (const w of words.split(' '))
        DIGIT_WORDS[w] = digit;
const REPEAT_WORDS = { double: 2, dabal: 2, dubal: 2, dubble: 2, dable: 2, triple: 3, tripal: 3, tripple: 3, tirpal: 3 };
/** Words around a number that don't make the line "more than digits". */
const FILLERS = new Set(['ji', 'jee', 'haan', 'han', 'hai', 'mera', 'meraa', 'number', 'nambar', 'nambr', 'mobile', 'mobail', 'phone', 'fon', 'ok', 'okay', 'aur', 'and', 'then', 'phir', 'fir', 'is', 'my']);
function tokens(text) {
    const ascii = text.replace(/[०-९]/g, (d) => String(d.charCodeAt(0) - 0x0966));
    return romanizeIndic(ascii).toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
}
/** Digits said in a line (longest unbroken run) and whether the line is nothing but a number. */
export function spokenDigits(text) {
    let best = '';
    let run = '';
    let repeat = 1;
    let only = true;
    for (const t of tokens(text)) {
        const d = /^\d+$/.test(t) ? t : DIGIT_WORDS[t];
        if (d !== undefined) {
            run += repeat > 1 ? d[0].repeat(repeat) + d.slice(1) : d;
            repeat = 1;
        }
        else if (REPEAT_WORDS[t]) {
            repeat = REPEAT_WORDS[t];
        }
        else {
            if (!FILLERS.has(t))
                only = false;
            if (run.length > best.length)
                best = run;
            run = '';
            repeat = 1;
        }
    }
    return { digits: run.length > best.length ? run : best, only };
}
const NOTE = '[System note, not said by the caller]';
/**
 * Note for the model about a caller line, plus the partial number to carry to
 * the next line (callers often say a number in groups, with pauses).
 */
export function phoneDigitsNote(line, partial = '') {
    const { digits: said, only } = spokenDigits(line);
    if (said.length < 3 || (said.length < 7 && !only))
        return { note: null, partial: '' };
    let digits = partial && only && said.length < 10 && partial.length + said.length <= 12 ? partial + said : said;
    if (digits.length === 11 && digits.startsWith('0'))
        digits = digits.slice(1);
    if (digits.length === 12 && digits.startsWith('91'))
        digits = digits.slice(2);
    if (digits.length === 10) {
        return {
            partial: '',
            note: `${NOTE} The transcript of what the caller said has exactly these 10 digits: ${digits.split('').join(' ')}. If this is a phone number, it is complete and has the right length. Trust these digits over what you heard: read back exactly these digits one by one, adding, removing or changing nothing, and do not mention how many digits it has.`,
        };
    }
    if (digits.length < 10) {
        return {
            partial: digits,
            note: `${NOTE} So far the transcript has these ${digits.length} digits: ${digits.split('').join(' ')}. If the caller is giving a 10-digit mobile number, it is not finished: say only a very short "ji, aage boliye" (in the caller's language) and let them continue. Do not read it back yet.`,
        };
    }
    return {
        partial: '',
        note: `${NOTE} The transcript of what the caller said has ${digits.length} digits: ${digits.split('').join(' ')}. If you asked for a 10-digit mobile number, a digit was misheard. Do not guess, do not drop digits and do not blame the caller: apologise briefly, say the line was unclear, and ask them to say the whole number again slowly.`,
    };
}
/** For the session instructions, so the model acts on the notes above. */
export const PHONE_NUMBER_RULES = [
    '## Phone numbers (overrides anything above)',
    '- Your own hearing of digits is unreliable. After the caller says a number you may get a "[System note]" with the digits from the transcript. It always wins over what you heard or remember, also when the caller corrected a number.',
    '- Never count digits yourself and never tell the caller a number is too long or too short unless a system note says so.',
    '- Never ask the caller to remove or add a digit. If a number is in doubt, ask for the whole number again.',
].join('\n');
//# sourceMappingURL=voice-digits.js.map